import { ChevronRight, MapPin, X } from "lucide-react";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, useBlocker, useLocation, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  findOrCreatePayee,
  formatCents,
  formatDay,
  initialKeypadState,
  keypadCents,
  keypadDisplay,
  keypadReducer,
  lastCategoryForPayee,
  monthOf,
  newId,
  roundLocation,
  saveTransaction,
  suggestPayees,
  todayIso,
  type AccountId,
  type CategoryId,
  type PayeeId,
} from "@budgeter/shared";
import { useApp, useBudget, useSnapshot, useSyncStatus } from "../../data/StoreContext";
import { accountsWithBalances, categoryName } from "../../data/selectors";
import { useLocationSetting } from "../../data/locationSetting";
import { useDefaultAccountId } from "../../layout/me";
import { BalancePill, balanceTone, Button, cn, Keypad } from "../../ui";
import { AccountPicker } from "../pickers/AccountPicker";
import { CategoryPicker, incomeLabels, type CategoryChoice } from "../pickers/CategoryPicker";
import { DatePickerSheet } from "../pickers/DatePickerSheet";
import { formatDistance } from "../pickers/payeeLists";
import { PayeePicker, type PayeePick } from "../pickers/PayeePicker";
import { useConfirm } from "../register/useConfirm";
import { markAddedHere } from "../transaction/localAdds";
import { useCurrentLocation } from "./useCurrentLocation";

type QuickPayee = Exclude<PayeePick, { kind: "transfer" }>;

const fieldRow =
  "flex min-h-[48px] w-full items-center gap-3 border-b border-line-soft px-3.5 text-left text-[15px] text-ink last:border-b-0";
const fieldLabel = "w-[72px] shrink-0 text-[13px] font-semibold text-muted";

/** Phone Quick Add (SPEC §11.3): keypad first, payee guessed from location, Save — Undo. */
export function QuickAddPage() {
  const { store, session } = useApp();
  const s = useSnapshot();
  const status = useSyncStatus();
  const meDefault = useDefaultAccountId();
  const [locationOn] = useLocationSetting();
  const here = useCurrentLocation(locationOn);
  const [params] = useSearchParams();
  const prefillParam = params.get("category");
  const prefill: CategoryChoice =
    prefillParam && s.categories.get(prefillParam) && !s.categories.get(prefillParam)?.deleted
      ? { categoryId: prefillParam as CategoryId }
      : null;

  const [keypad, dispatch] = useReducer(keypadReducer, undefined, () => initialKeypadState());
  const [chosenPayee, setChosenPayee] = useState<QuickPayee | null | undefined>(undefined);
  const [chosenCategory, setChosenCategory] = useState<CategoryChoice | undefined>(undefined);
  const [chosenAccount, setChosenAccount] = useState<AccountId | null>(null);
  const [date, setDate] = useState(todayIso);
  const [memo, setMemo] = useState("");
  const [sheet, setSheet] = useState<"payee" | "category" | "account" | "date" | null>(null);

  const suggestions = useMemo(
    () =>
      here
        ? suggestPayees({
            here,
            transactions: [...s.transactions.values()],
            payees: [...s.payees.values()],
          })
        : null,
    [here, s.transactions, s.payees],
  );

  // Untouched fields follow the best guesses; a choice the user made sticks until Save.
  const payee: QuickPayee | null =
    chosenPayee !== undefined
      ? chosenPayee
      : suggestions?.guess
        ? { kind: "payee", payeeId: suggestions.guess }
        : null;
  const payeeId: PayeeId | null = payee?.kind === "payee" ? payee.payeeId : null;
  const category: CategoryChoice =
    chosenCategory !== undefined ? chosenCategory : (prefill ?? payeeCategory(payeeId));

  function payeeCategory(id: PayeeId | null): CategoryChoice {
    const last = id ? lastCategoryForPayee(s.transactions.values(), id) : null;
    return last ? { categoryId: last } : null;
  }

  const { onBudget } = accountsWithBalances(s);
  const accountId: AccountId | null =
    (chosenAccount && s.accounts.get(chosenAccount) ? chosenAccount : null) ??
    (meDefault && onBudget.some((a) => a.id === meDefault) ? (meDefault as AccountId) : null) ??
    onBudget[0]?.id ??
    null;
  const accountLabel = accountId ? (s.accounts.get(accountId)?.name ?? "") : "";

  const budget = useBudget(monthOf(date));
  const amount = keypadCents(keypad);
  const inflow = keypad.sign === "inflow";
  const { confirm, dialog } = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  // Set on save, so leaving afterwards doesn't ask to discard what was just saved.
  const saved = useRef(false);
  // Leaving with an amount typed (Close or Back) asks first.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !saved.current && amount !== 0 && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }
    void confirm({
      title: "Discard this entry?",
      message: "The amount you typed hasn't been saved.",
      confirmLabel: "Discard",
      cancelLabel: "Keep editing",
      danger: true,
    }).then((ok) => (ok ? blocker.proceed() : blocker.reset()));
    // Runs once per blocked navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);
  const payeeLabel =
    payee?.kind === "payee" ? (s.payees.get(payee.payeeId)?.name ?? "") : (payee?.name ?? "");
  const nearby = payeeId ? suggestions?.nearby.find((n) => n.payeeId === payeeId) : undefined;
  const payeeHint = !nearby
    ? null
    : chosenPayee === undefined
      ? `Best guess · you're ${formatDistance(nearby.distanceMeters)} away`
      : `${formatDistance(nearby.distanceMeters)} away`;

  let categoryText = "";
  let categoryPill: { cents: number; tone: ReturnType<typeof balanceTone> } | null = null;
  if (category && "categoryId" in category) {
    categoryText = categoryName(s, category.categoryId);
    const cell = budget.cell(category.categoryId, monthOf(date));
    const carry = s.categories.get(category.categoryId)?.overspend === "carry";
    categoryPill = {
      cents: cell.available,
      tone: balanceTone(cell.available, cell.budgeted, carry),
    };
  } else if (category && "income" in category) {
    categoryText = incomeLabels[category.income];
  }

  function save() {
    if (amount === 0 || !accountId) {
      return;
    }
    const description = `Added ${payeeLabel || "transaction"} ${formatCents(amount)}`;
    const change = store.run(description, (w) => {
      const id = newId("transaction");
      const resolvedPayee =
        payee?.kind === "new" ? findOrCreatePayee(w, payee.name).id : (payeeId ?? undefined);
      saveTransaction(w, {
        id,
        accountId,
        date,
        amount,
        payeeId: resolvedPayee,
        categoryId: category && "categoryId" in category ? category.categoryId : undefined,
        income: category && "income" in category ? category.income : undefined,
        memo: memo.trim(),
        location: here ? roundLocation(here) : undefined,
        createdBy: session.userId,
      });
      return id;
    });
    markAddedHere(change.result);
    toast.success(`Saved ${formatCents(amount)}${categoryText ? ` · ${categoryText}` : ""}`, {
      action: { label: "Undo", onClick: () => store.undo(change, description) },
    });
    saved.current = true;
    // Back to where Quick Add was opened from; Home when it was opened directly.
    if (location.key === "default") {
      void navigate("/", { replace: true });
    } else {
      void navigate(-1);
    }
  }

  const today = todayIso();
  const offline = status.connection !== "online";

  return (
    <div className="-mb-28 mx-auto flex min-h-[calc(100dvh-env(safe-area-inset-top))] max-w-[520px] flex-col gap-3 px-4 pt-3.5 pb-4">
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/"
          className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-line bg-surface px-3.5 text-[15px] font-bold text-ink no-underline"
        >
          <X size={18} strokeWidth={2.4} aria-hidden />
          Close
        </Link>
        {(offline || status.hasUnsynced) && (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 rounded-full bg-chip px-2.5 py-1.5 text-[12.5px] text-muted"
          >
            <span aria-hidden className="size-[7px] rounded-full bg-[#C9A227]" />
            {offline ? "Offline · saved on this phone" : "Waiting to sync"}
          </span>
        )}
      </div>

      <h1 className="sr-only">Quick Add</h1>

      <div className="flex flex-col items-center gap-2 pt-1.5">
        <div role="radiogroup" aria-label="Direction" className="flex rounded-full bg-chip p-[3px]">
          {(["outflow", "inflow"] as const).map((sign) => (
            <button
              key={sign}
              type="button"
              role="radio"
              aria-checked={keypad.sign === sign}
              onClick={() => keypad.sign !== sign && dispatch({ type: "toggleSign" })}
              className={cn(
                "min-h-11 rounded-full px-4 text-[13.5px] font-bold text-ink",
                keypad.sign === sign && "bg-surface shadow-soft",
              )}
            >
              {sign === "outflow" ? "Outflow" : "Inflow"}
            </button>
          ))}
        </div>
        <output
          aria-label="Amount"
          aria-live="polite"
          className={cn(
            "num text-[52px] leading-tight font-extrabold tracking-[-1px]",
            inflow ? "text-pos" : "text-ink",
          )}
        >
          {amount === 0 ? "" : inflow ? "+" : "−"}
          {keypadDisplay(keypad)}
        </output>
      </div>

      <div className="flex flex-col overflow-hidden rounded-2xl border border-line bg-surface">
        <button type="button" className={fieldRow} onClick={() => setSheet("payee")}>
          <span className={fieldLabel}>Payee</span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className={cn("truncate font-bold", !payeeLabel && "font-normal text-muted")}>
              {payeeLabel || "Choose payee"}
            </span>
            {payeeHint && (
              <span className="flex items-center gap-1 text-[12px] text-muted">
                <MapPin size={11} strokeWidth={2.4} aria-hidden />
                {payeeHint}
              </span>
            )}
          </span>
          <ChevronRight size={16} className="text-faint" aria-hidden />
        </button>
        <button type="button" className={fieldRow} onClick={() => setSheet("category")}>
          <span className={fieldLabel}>Category</span>
          <span
            className={cn(
              "min-w-0 flex-1 truncate font-bold",
              !categoryText && "font-normal text-muted",
            )}
          >
            {categoryText || "Choose category"}
          </span>
          {categoryPill && (
            <BalancePill cents={categoryPill.cents} tone={categoryPill.tone} className="min-w-0" />
          )}
        </button>
        <button type="button" className={fieldRow} onClick={() => setSheet("account")}>
          <span className={fieldLabel}>Account</span>
          <span className="min-w-0 flex-1 truncate">{accountLabel || "No account yet"}</span>
          <ChevronRight size={16} className="text-faint" aria-hidden />
        </button>
        <button type="button" className={fieldRow} onClick={() => setSheet("date")}>
          <span className={fieldLabel}>Date</span>
          <span className="min-w-0 flex-1 truncate">
            {date === today ? `Today, ${formatDay(date)}` : formatDay(date)}
          </span>
          <ChevronRight size={16} className="text-faint" aria-hidden />
        </button>
        <label className={cn(fieldRow, "cursor-text")}>
          <span className={fieldLabel}>Memo</span>
          <input
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="Add a note"
            maxLength={500}
            className="min-w-0 flex-1 border-0 bg-transparent text-[16px] text-ink outline-none placeholder:text-faint"
          />
        </label>
      </div>

      <div className="mt-auto flex flex-col gap-2">
        <Keypad dispatch={dispatch} />
        <Button
          variant="primary"
          size="lg"
          className="h-14 text-[18px]"
          disabled={amount === 0 || !accountId}
          onClick={save}
        >
          Save
        </Button>
      </div>

      {dialog}
      <PayeePicker
        open={sheet === "payee"}
        onOpenChange={(o) => setSheet(o ? "payee" : null)}
        value={payeeId}
        suggestions={suggestions}
        onPick={(p) => {
          if (p.kind !== "transfer") {
            setChosenPayee(p);
          }
        }}
      />
      <CategoryPicker
        open={sheet === "category"}
        onOpenChange={(o) => setSheet(o ? "category" : null)}
        value={category}
        month={monthOf(date)}
        onChange={setChosenCategory}
      />
      <AccountPicker
        open={sheet === "account"}
        onOpenChange={(o) => setSheet(o ? "account" : null)}
        value={accountId}
        onPick={setChosenAccount}
        onBudgetOnly
      />
      <DatePickerSheet
        open={sheet === "date"}
        onOpenChange={(o) => setSheet(o ? "date" : null)}
        value={date}
        onPick={setDate}
      />
    </div>
  );
}
